import { defineComponent as _defineComponent } from 'vue'
import {
  useI18n,
} from "@/composables/useI18n";

import {
  ref,
} from "vue";

import type {
  Customer,
  FetchCustomersParam,
} from "@/components/customers/scripts";

import {
  CustomerSearchFilter
} from '@/components/customers/children';

import {
  CustomerState,
} from "@/Enums";

import {
  CUSTOMER_ID_STORAGE_KEY,
  deleteCustomer,
  fetchCustomers,
} from "@/components/customers/scripts";

import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";

import ConfirmationDialog from "@/components/base/ConfirmationDialog.vue";

import {
  personFullName,
} from "@/scripts/Common/helpers";

import {
  useCustomerStateMap,
} from "@/components/customers/composables/useCustomerStateMap";

import {
  useDataTableDefaults,
} from "@/composables/useDataTableDefaults";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";
import {getAccessRights} from "@/authorization";
import {customerDataPolicies} from "@/authorization/policies";
import {isAbortError} from "@/scripts/Common/HelperFunctions";


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerManagement',
  emits: [
  'onSelectCustomerDetail',
  'onCreateNewCustomer',
  'onFetchError'
],
  setup(__props, { emit: emits }) {

const {
  t,
} = useI18n();

const {
  hasEditRights
} = getAccessRights(customerDataPolicies);

const {
  pagination,
  dataTableFooter
} = useDataTableDefaults();

const {
  customerStateMap
} = useCustomerStateMap();



const customers = ref<Customer[]>([]);
const selectedCustomerId = ref<number>();
const isLoading = ref(false);
const dataTableOptions = ref();

const deleteCustomerDialog = ref();

const hasNoEditRights = !hasEditRights;

let fetchCustomerFilterArgs : { customerState?: CustomerState, companyName?: string, phoneNumber?: string, email?: string, contactName?: string } = {};

let fetchCustomersAbortController : AbortController = new AbortController();

const headers = [
  {
    align: "start",
    value: "actions",
    sortable: false,
    width: "1%"
  },
  {
    align: "start",
    text: t("company_name"),
    filterable: true,
    value: "companyName",
  },
  {
    align: "start",
    text: t("contact_employee_full_name"),
    filterable: true,
    value: "contactEmployee.fullName",
  },
  {
    align: "start",
    text: t("phone"),
    sortable: false,
    value: "contactEmployee.phoneNumber",
  },
  {
    align: "start",
    text: t("email"),
    filterable: true,
    value: "contactEmployee.email"
  },
  {
    align: "start",
    text: t("state"),
    filterable: false,
    sortable: false,
    value: "state"
  },
  {
    align: "start",
    text: t("action"),
    filterable: false,
    sortable: false,
    value: "edit"
  }
];

const headerSortValueToSortColumn = (value: string) => {
  switch (value) {
    case "companyName":
      return "companyname";
    case "contactEmployee.fullName":
      return "contactname";
    case "contactEmployee.email":
      return "email";
    default:
      return "companyname";
  }
}

const onDeleteCustomer = async () => {
  if (!selectedCustomerId.value) return;

  const {error} = await deleteCustomer(selectedCustomerId.value);
  selectedCustomerId.value = undefined;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  loadCustomers();
}

const loadCustomers = async () => {
  const _sortColumn = dataTableOptions.value.sortBy;
  const _sortDirection = dataTableOptions.value.sortDesc;

  const fetchArgs: FetchCustomersParam = {
    pageNumber: dataTableOptions.value.page,
    pageSize: dataTableOptions.value.itemsPerPage
  };

  // add sorting, if set
  if(_sortColumn.length === 1 && _sortDirection.length === 1){ // if use multisort, this could have length > 1, but we only use single sort
    fetchArgs.sortColumn = headerSortValueToSortColumn(_sortColumn[0]);
    fetchArgs.sortDirection = _sortDirection[0] ? "desc" : "asc";
  }

  fetchCustomersAbortController.abort();
  fetchCustomersAbortController = new AbortController();

  isLoading.value = true;

  const {response, error, totalItemCount} = await fetchCustomers({...fetchArgs,...fetchCustomerFilterArgs}, fetchCustomersAbortController.signal);

  if(isAbortError(error)) {
    return;
  };

  isLoading.value = false;

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  customers.value = response ?? [];
  pagination.value.totalItemCount = totalItemCount ?? 0;
}

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
};

const onHandleCreate = () => {
  emits('onCreateNewCustomer');
}

const onHandleRefresh = () => {
  loadCustomers()
}

const onSelectUserDetails = (customerId: number) => {
  saveItemToStorage(customerId, CUSTOMER_ID_STORAGE_KEY);
  emits('onSelectCustomerDetail');
}

const onHandleDeleteCustomer = (customerId: number) => {
  selectedCustomerId.value = customerId;
  deleteCustomerDialog.value.Show();
}

const onUpdateDataTabelOptions = () => {
  loadCustomers();
}

const onSearchFilterChange = async (searchFilter: { customerState?: CustomerState, companyName?: string, phoneNumber?: string, email?: string, contactName?: string }) =>{
  fetchCustomerFilterArgs = searchFilter;

  // if we are on page 1, we can just reload the data
  if(dataTableOptions.value.page === 1){
    await loadCustomers();
    return;
  }

  // switch to page 1, wich will trigger loadCustomers() in onUpdateDataTabelOptions
  dataTableOptions.value.page = 1;
}


return { __sfc: true,t, hasEditRights, pagination, dataTableFooter, customerStateMap, emits, customers, selectedCustomerId, isLoading, dataTableOptions, deleteCustomerDialog, hasNoEditRights, fetchCustomerFilterArgs, fetchCustomersAbortController, headers, headerSortValueToSortColumn, onDeleteCustomer, loadCustomers, onHandleFetchError, onHandleCreate, onHandleRefresh, onSelectUserDetails, onHandleDeleteCustomer, onUpdateDataTabelOptions, onSearchFilterChange, CustomerSearchFilter, CustomerState, FloatingActionButton, ConfirmationDialog, personFullName }
}

})